import { useEffect, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { initTelemetry } from "@/telemetry";
import { installGlobalErrorCapture } from "@/telemetry/capture";
import { useTelemetryRoute } from "@/telemetry/useTelemetryRoute";

import { Header } from "./Header";
import { PlatformAlertBanner } from "./PlatformAlertBanner";
import { SideNav } from "./SideNav";

export function AppShell(): ReactNode {
  const { t } = useTranslation("common");

  // Telemetry is mounted here, and only here. The shell is inside
  // ProtectedRoute, so by this point there is a token to authenticate the
  // ingest call with and an identity for the server to stamp. Both calls are
  // idempotent and both no-op entirely when VITE_TELEMETRY_ENABLED=false.
  useEffect(() => {
    initTelemetry();
    installGlobalErrorCapture();
  }, []);
  useTelemetryRoute();

  // The Farm Console (and the legacy map) pin to the viewport and own their
  // internal scrolling. That's a shell-level concern — an inner element can't
  // opt its ancestors into `h-screen` — so it stays keyed off the pathname.
  //
  // The Farm Timeline is the third such surface and it was missed. Without a
  // ceiling the shell is only `min-h-screen`, so a `<Page width="bleed">`
  // whose `h-full` resolves against an auto-height parent has unbounded room:
  // MapLibre sizes its canvas to the container, the container grows, the
  // ResizeObserver calls `map.resize()`, and it runs away. Measured on prod
  // at 1600x950 the map reached 15983px tall against a 950px viewport, which
  // put the farm several screens below the fold and left the visible strip
  // showing empty desert. Adding the prefix here is the whole fix.
  //
  // Page *padding* is no longer decided here. <main> used to apply
  // `px-4 py-6`, which meant every page that added its own `p-6` double-padded
  // and every /settings/* page triple-padded. <Page> owns the inset now, so a
  // page's declared width is also its declared padding.
  const { pathname } = useLocation();
  const viewportPinned =
    pathname.startsWith("/labs/map") ||
    pathname.startsWith("/timeline") ||
    pathname.startsWith("/farm-health");
  return (
    <div
      className={
        viewportPinned ? "flex h-screen flex-col bg-ap-bg" : "flex min-h-screen flex-col bg-ap-bg"
      }
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:bg-white focus:px-3 focus:py-1 focus:text-sm focus:shadow-card"
      >
        {t("nav.skipToContent")}
      </a>
      {/* Above <Header> on purpose: a platform-wide failure outranks the
          page you are on, and this is the one strip that must not scroll
          away or sit inside a route that a tenant user never visits. */}
      <PlatformAlertBanner />
      <Header />
      <div className="flex min-h-0 w-full flex-1 gap-0">
        <SideNav />
        <main
          id="main-content"
          className={
            viewportPinned ? "min-w-0 flex-1 overflow-hidden" : "min-w-0 flex-1 overflow-x-hidden"
          }
        >
          {/* Inside <main>, so a page that throws leaves the header, the
              nav and the platform alert bar standing. Before this the app
              had no boundary at all and one bad render blanked the whole
              document. */}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
